<template>
  <div>
    <old-date-convert-banner v-if="useOldFunctionalityForDays" :id="festival.id" />
    <h1 class="mt-2 mb-4 font-sans text-n-gray-dark">Festival</h1>
    <form class="md:max-w-lg" method="POST" v-if="!saving">
      <div class="mb-12">
        <label for="form.title" ref="title">
          Title <span class="text-red">*</span>
        </label>
        <input id="title" type="text" name="title"
               v-validate="'required'"
               v-model="form.title">
        <span v-show="errors.has('title')" class="error text-error-red text-lg">{{ errors.first('title') }}</span>
      </div>

      <div class="mb-12">
        <label for="slug" ref="slug">
          Slug <span class="text-red">*</span>
          <div class="text-sm font-sans-book">Enter the slug for the Festival entry in Craft. This is critical as it establishes the relationship between all events in this festival in HEMS with the Craft powered website.</div>
        </label>
        <input id="slug" type="text" name="slug"
               v-validate="'required'"
               v-model="form.slug">
        <span v-show="errors.has('slug')" class="error text-error-red text-lg">{{ errors.first('slug') }}</span>
      </div>

      <div class="mb-12">
        <label for="contract" ref="contract">
          Contract <span class="text-red">*</span>
        </label>
        <select
          id="contract"
          name="contract"
          v-validate="'required'"
          v-model="form.contractId">
          <option v-for="contract in contracts" :value="contract.id" :key="contract.id">{{ contract.title }}</option>
        </select>
        <span v-show="errors.has('contract')" class="error text-error-red text-lg">{{ errors.first('contract') }}</span>
      </div>

      <div class="mb-12">
        <label for="hashtag" ref="hashtag">
          Hashtag <span class="text-red">*</span>
          <div class="text-sm font-sans-book">Please do not include the Hashtag Symbol "#"</div>
        </label>
        <input id="hashtag" type="text" name="hashtag"
               v-validate="'required'"
               v-model="form.hashtag">
        <span v-show="errors.has('hashtag')" class="error text-error-red text-lg">{{ errors.first('hashtag') }}</span>
      </div>

      <div class="mb-12">
        <label for="thankYouUrl" ref="thankYouUrl">
          Thank you url <span class="text-red">*</span>
          <div class="text-sm font-sans-book">This is the URL to the thank you page on the live website that the user will be redirected to after checking out using SquadUP</div>
        </label>
        <input id="thankYouUrl" type="text" name="thankYouUrl"
               v-validate="'required'"
               v-model="form.thankYouUrl">
        <span v-show="errors.has('thankYouUrl')" class="error text-error-red text-lg">{{ errors.first('thankYouUrl') }}</span>
      </div>

      <time-zones v-model="form.timezone" label="Festival time zone"></time-zones>

      <div class="mb-12" v-if="useOldFunctionalityForDays">
        <label for="days" ref="days">
          Days <span class="text-red">*</span>
          <span class="text-sm font-sans-book block">
            Please enter the days that you would Hosts to be able to select from to create their events. If you enter a new date that is not in chronological order as the ones that preceded it, it will be ordered properly once you save the festival.
          </span>
        </label>

        <day-selector
          id="days"
          name="days"
          v-validate="'required'"
          v-for="(day, index) in form.days"
          :key="day.id"
          v-model="form.days[index]"
          v-on:remove-day="removeDay"
          :arrayIndex="index"
        />

        <span v-show="errors.has('days')" class="error text-error-red text-lg">{{ errors.first('days') }}</span>

        <div class="py-3">
          <a href="#" class="text-black" @click.stop.prevent="addDay">Add Day</a>
        </div>
      </div>
      <div v-else>
        <div class="mb-12">
          <label for="startAt">
            Festival Start Date <span class="text-red">*</span>
          </label>
          <lab-admin-date-range-picker name="festival start date" v-validate="'required'" id="startAt" v-model="form.startAt" />
          <span v-show="errors.has('festival start date')" class="error text-error-red text-lg">{{ errors.first('festival start date') }}</span>
        </div>

        <div class="mb-12">
          <label for="endAt">
            Festival End Date <span class="text-red">*</span>
          </label>
          <lab-admin-date-range-picker :restricted-before-date="form.startAt" name="festival end date" v-validate="'required'" id="endAt" v-model="form.endAt" />
          <span v-show="errors.has('festival end date')" class="error text-error-red text-lg">{{ errors.first('festival end date') }}</span>
        </div>

        <div class="mb-12" v-if="form.startAt && form.endAt">
          <label for="endAt">
            Festival Exclusion Days
            <span class="text-sm font-sans-book block">
              Use this to block out any days between the festival start and end dates from collaborators.
              Dates highlighted in blue aren't excluded.
            </span>
          </label>
          <div class="mt-5">
            <lab-admin-date-exclusion-picker v-model="form.days" :start-at="form.startAt" :end-at="form.endAt" />
          </div>
        </div>
      </div>

      <div class="mb-12">
        <label for="gaStartDay" ref="gaStartDay">
          General Admission Start Day <span class="text-red">*</span>
        </label>

        <datepicker id="gaStartDay" name="gaStartDay"
                    v-validate="'required'"
                    :format="'yyyy-MM-dd'"
                    v-model="form.gaStartDay"></datepicker>

        <span v-show="errors.has('gaStartDay')" class="error text-error-red text-lg">{{ errors.first('gaStartDay') }}</span>

      </div>

      <div class="mb-12">
        <label for="loadGaStartTime" ref="loadGaStartTime">
          General Admission Start Time <span class="text-red">*</span>
          <div class="text-sm font-sans-book">Note time zone will match the time zone set for the festival. </div>
        </label>

        <time-picker name="loadGaStartTime"
                     v-validate="'required'"
                     v-model="form.loadGaStartTime"></time-picker>

        <span v-show="errors.has('loadGaStartTime')" class="error text-error-red text-lg">{{ errors.first('loadGaStartTime') }}</span>
      </div>

      <div class="mb-12">
        <label for="gaEndDay" ref="gaEndDay">
          General Admission End Day <span class="text-red">*</span>
        </label>

        <datepicker id="gaEndDay" name="gaEndDay"
                    v-validate="'required'"
                    :format="'yyyy-MM-dd'"
                    v-model="form.gaEndDay"></datepicker>

        <span v-show="errors.has('gaEndDay')" class="error text-error-red text-lg">{{ errors.first('gaEndDay') }}</span>
      </div>

      <div class="mb-12">
        <label for="loadGaEndTime" ref="loadGaEndTime">
          General Admission End Time <span class="text-red">*</span>
          <div class="text-sm font-sans-book">Note time zone will match the time zone set for the festival. </div>
        </label>

        <time-picker name="loadGaEndTime"
                     v-validate="'required'"
                     v-model="form.loadGaEndTime"></time-picker>

        <span v-show="errors.has('loadGaEndTime')" class="error text-error-red text-lg">{{ errors.first('loadGaEndTime') }}</span>
      </div>

      <!--
              <div class="mb-12">
                  <label for="earlyBirdStartDay" ref="earlyBirdStartDay">
                      Early Bird Start Day <span class="text-red">*</span>
                  </label>

                  <datepicker id="earlyBirdStartDay" name="earlyBirdStartDay"
                      v-validate="'required'"
                      :format="'yyyy-MM-dd'"
                      v-model="form.earlyBirdStartDay"
                  ></datepicker>

                  <span v-show="errors.has('earlyBirdStartDay')" class="error text-error-red text-lg">{{ errors.first('earlyBirdStartDay') }}</span>
              </div>

              <div class="mb-12">
                  <label for="loadEarlyBirdStartTime" ref="loadEarlyBirdStartTime">
                      Early Bird Start Time <span class="text-red">*</span>
                      <div class="text-sm font-sans-book">Note time zone will match the time zone set for the festival. </div>
                  </label>

                  <time-picker name="loadEarlyBirdStartTime"
                      v-validate="'required'"
                      v-model="form.loadEarlyBirdStartTime"></time-picker>

                  <span v-show="errors.has('loadEarlyBirdStartTime')" class="error text-error-red text-lg">{{ errors.first('loadEarlyBirdStartTime') }}</span>
              </div>

              <div class="mb-12">
                  <label for="earlyBirdEndDay" ref="earlyBirdEndDay">
                      Early Bird End Day <span class="text-red">*</span>
                  </label>

                  <datepicker id="earlyBirdEndDay" name="earlyBirdEndDay"
                      v-validate="'required'"
                      :format="'yyyy-MM-dd'"
                      v-model="form.earlyBirdEndDay"></datepicker>

                  <span v-show="errors.has('earlyBirdEndDay')" class="error text-error-red text-lg">{{ errors.first('earlyBirdEndDay') }}</span>
              </div>

              <div class="mb-12">
                  <label for="loadEarlyBirdEndTime" ref="loadEarlyBirdEndTime">
                      Early Bird End Time <span class="text-red">*</span>
                      <div class="text-sm font-sans-book">Note time zone will match the time zone set for the festival. </div>
                  </label>

                  <time-picker name="loadEarlyBirdEndTime"
                      v-validate="'required'"
                      v-model="form.loadEarlyBirdEndTime"></time-picker>

                  <span v-show="errors.has('loadEarlyBirdEndTime')" class="error text-error-red text-lg">{{ errors.first('loadEarlyBirdEndTime') }}</span>
              </div>

              <div class="mb-12">
                  <label for="earlyBirdDiscount" ref="earlyBirdDiscount">
                      Early Bird Discount <span class="text-red">*</span>
                      <div class="text-sm font-sans-book">Enter a number for a percentage discount. Example: 25 = 25%.</div>
                  </label>
                  <input id="earlyBirdDiscount" type="text" name="earlyBirdDiscount"
                      v-validate="'required'"
                      v-model="form.earlyBirdDiscount">
                  <span v-show="errors.has('earlyBirdDiscount')" class="error text-error-red text-lg">{{ errors.first('earlyBirdDiscount') }}</span>
              </div>
      -->
      <div class="mb-12">
        <label for="allowEventCreation">Allow Event Creation</label>
        <div class="select-box">
          <label class="text-base font-normal" for="allowEventCreation">
            <input type="checkbox" id="allowEventCreation" v-model="form.allowEventCreation">
            <span class="check inline-block mr-2"></span> Yes
          </label>
        </div>
      </div>

      <div class="mb-4">
        <button class="button" @click.stop.prevent="saveFestival">Save</button>
      </div>

    </form>
    <div class="text-2xl text-center mb-4" v-else>Saving Event...</div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import timeHelper from "../../mixins/time-helper";
import Datepicker from "vuejs-datepicker";
import TimePicker from "../global/TimePicker.vue";
import DaySelector from "./DaySelector.vue";
import TimeZones from "../global/TimeZones.vue";
import validation from "../../mixins/validation";
import LabAdminDateRangePicker from "../global/LabAdminDateRangePicker.vue";
import LabAdminDateExclusionPicker from "../global/LabAdminDateExclusionPicker.vue";
import OldDateConvertBanner from "./temp/OldDateConvertBanner.vue";

export default {
  name: "FestivalForm",
  title: "Festival Edit",
  mixins: [timeHelper, validation],
  components: {
    OldDateConvertBanner,
    LabAdminDateExclusionPicker,
    LabAdminDateRangePicker,
    TimePicker,
    Datepicker,
    DaySelector,
    TimeZones
  },
  data: () => ({
    form: {
      title: null,
      slug: null,
      contractId: null,
      acceptDonations: false,
      hashtag: null,
      thankYouUrl: null,
      timezone: "America/New_York",
      days: [],
      gaStartDay: null,
      gaStartTime: {},
      loadGaStartTime: "",
      gaEndDay: null,
      gaEndTime: {},
      loadGaEndTime: "",
      allowEventCreation: false,
      startAt: null,
      endAt: null
    },
    saving: false,
    contracts: []
  }),
  computed: {
    ...mapGetters({
      festival: "festival"
    }),
    isEditMode() {
      return !!this.$route.params.id;
    },
    useOldFunctionalityForDays() {
      if (this.isEditMode) {
        return !this.festival.endAt && !this.festival.startAt;
      }
      return false;
    }
  },
  watch: {
    festival(newFestival, oldFestival) {
      if (newFestival.id && !oldFestival.id) {
        this.assignFields()
      }
    }
  },
  methods: {
    saveFestival() {
      this.$validator.validateAll().then(result => {
        if (this.form.days.length < 1 && this.useOldFunctionalityForDays) {
          this.errors.add({
            field: "days",
            msg: "At least one day is required"
          });
        }

        if (!result || this.errors.items.length > 0) {
          this.gotoFirstValidationError();
          return;
        }

        // Format date for submission
        this.form.gaStartDay = this.formatDate(this.form.gaStartDay);
        this.form.gaEndDay = this.formatDate(this.form.gaEndDay);
        // this.form.earlyBirdStartDay = this.formatDate(
        //     this.form.earlyBirdStartDay
        // );
        // this.form.earlyBirdEndDay = this.formatDate(
        //     this.form.earlyBirdEndDay
        // );

        // Format times
        this.form.gaStartTime = this.formatTo24HourTime(
          this.form.loadGaStartTime
        );
        this.form.gaEndTime = this.formatTo24HourTime(
          this.form.loadGaEndTime
        );
        // this.form.earlyBirdStartTime = this.formatTo24HourTime(
        //     this.form.loadEarlyBirdStartTime
        // );
        // this.form.earlyBirdEndTime = this.formatTo24HourTime(
        //     this.form.loadEarlyBirdEndTime
        // );

        if (this.useOldFunctionalityForDays) {
          _.forEach(
            this.form.days,
            function (input, index) {
              this.form.days[index].day = this.formatDate(input.day);
            }.bind(this)
          );
        }

        // Save data
        if (this.isEditMode) {
          let route = this.$laroute.route("api.festivals.update", {
            festival: this.festival.id
          });

          axios.put(route, this.form).then(response => {
            this.$router.push({ name: "festivals" });
            this.$toasted.show("Festival has been updated");
          });
        } else {
          let route = this.$laroute.route("api.festivals.store");

          axios.post(route, this.form).then(response => {
            this.$router.push({ name: "festivals" });
            this.$toasted.show("Festival has been created");
          });
        }
      });
    },

    getContracts() {
      let route = this.$laroute.route("api.contracts.index");

      axios.get(route).then(response => {
        this.contracts = response.data.data;
      });
    },

    addDay() {
      const id = Math.random().toString(36).substring(7);

      this.form.days.push({
        id: id,
        day: null
      });
    },

    removeDay(index) {
      this.form.days.splice(index, 1);
    },

    assignFields() {
      const form = _.cloneDeep(this.festival);
      this.form = Object.assign({}, this.form, form);

      if (! this.useOldFunctionalityForDays && form.days.length < 1) {
        this.form.days = []
      }

      this.form.gaStartDay = new Date(
        `${this.festival.gaStartDay}T${this.festival.gaStartTime}`
      );
      this.form.gaEndDay = new Date(
        `${this.festival.gaEndDay}T${this.festival.gaEndTime}`
      );
      // this.form.earlyBirdStartDay = new Date(
      //     `${this.festival.earlyBirdStartDay}T${this.festival.earlyBirdStartTime}`
      // );
      // this.form.earlyBirdEndDay = new Date(
      //     `${this.festival.earlyBirdEndDay}T${this.festival.earlyBirdEndTime}`
      // );

      this.form.loadGaStartTime = this.formatTo12HourTime(
        this.form.gaStartTime
      );
      this.form.loadGaEndTime = this.formatTo12HourTime(
        this.form.gaEndTime
      );
      // this.form.loadEarlyBirdStartTime = this.formatTo12HourTime(
      //     this.form.earlyBirdStartTime
      // );
      // this.form.loadEarlyBirdEndTime = this.formatTo12HourTime(
      //     this.form.earlyBirdEndTime
      // );
    },

    clearForm() {
      this.form.title = null;
      this.form.slug = null;
      this.errors.clear();
    }
  },
  mounted() {
    this.getContracts();

    if (this.isEditMode && this.festival.id) {
      this.assignFields();
    }
  }
};
</script>
